import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { isChildItemActive } from "@/config/sidebar-nav";
import { useSettingsNavSections } from "./use-settings-nav-sections";

/**
 * Settings sub-navigation (2c). Settings lives behind the footer gear, so
 * this is the only place its pages are listed: a sectioned side column from
 * `md` up, and a single scrollable pill row below it.
 */
export function SettingsNav({ className }: { className?: string }) {
  const { t } = useTranslation("nav");
  const { pathname } = useLocation();
  const sections = useSettingsNavSections();
  const siblingUrls = useMemo(
    () => sections.flatMap((s) => s.items.map((i) => i.url)),
    [sections],
  );

  return (
    <nav aria-label={t("settings")} className={cn("min-w-0", className)}>
      {/* Mobile: one flat, horizontally scrollable pill row. */}
      <ul className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none] md:hidden">
        {sections
          .flatMap((s) => s.items)
          .map((item) => {
            const active = isChildItemActive(pathname, item.url, siblingUrls);
            return (
              <li key={item.url} className="shrink-0">
                <Link
                  to={item.url}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "inline-flex h-8 items-center rounded-full border px-3 text-[13px] font-medium transition-colors",
                    active
                      ? "border-g-ink-900 bg-g-ink-900 text-g-bg-card"
                      : "border-g-line text-g-ink-500 hover:bg-g-primary-wash hover:text-g-ink-900",
                  )}
                >
                  {t(item.titleKey)}
                </Link>
              </li>
            );
          })}
      </ul>

      {/* Desktop: sectioned side column. */}
      <div className="hidden md:block">
        <p className="mb-3 px-2 font-display text-lg tracking-display text-g-ink-900">
          {t("settings")}
        </p>
        <div className="space-y-5">
          {sections.map((section) => (
            <div key={section.id}>
              <p className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-g-ink-500">
                {t(`settingsSections.${section.id}`)}
              </p>
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const active = isChildItemActive(
                    pathname,
                    item.url,
                    siblingUrls,
                  );
                  return (
                    <li key={item.url}>
                      <Link
                        to={item.url}
                        aria-current={active ? "page" : undefined}
                        className={cn(
                          "flex h-8 items-center rounded-md px-2 text-[13px] transition-colors",
                          active
                            ? "bg-g-primary-wash font-medium text-g-ink-900"
                            : "text-g-ink-500 hover:bg-g-primary-wash hover:text-g-ink-900",
                        )}
                      >
                        {t(item.titleKey)}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
}
